import { unwrap, type IndustryTemplateKey, type SetupDraftIn } from "@sagot/api-client";
import { useQuery } from "@tanstack/react-query";
import { useDebounced } from "../lib/slug";
import { queryKeys } from "../lib/query-keys";
import { useApi } from "../session/session-provider";

type Goal = NonNullable<SetupDraftIn["goals"]>[number];

const GOALS_DEBOUNCE_MS = 300;
const TEMPLATE_KEY_INDEX = 2;

/**
 * `GET /v1/industry-templates/{key}/preview` — what the template sets up (assistant, starter
 * questions, audiences, pipeline, actions), shaped by the chosen goals. Public + cached per
 * template/language/goals. Goal toggles are debounced and keep the previous preview of the SAME
 * template on screen; switching template starts from nothing (the caller shows a skeleton).
 */
export function useTemplatePreview(templateKey: string | undefined, goals: readonly string[], language: string) {
  const api = useApi();
  const goalsKey = useDebounced([...goals].sort().join(","), GOALS_DEBOUNCE_MS);
  const lang = language === "fil" ? "fil" : "en";
  return useQuery({
    queryKey: queryKeys.templatePreview(templateKey ?? "none", lang, goalsKey),
    queryFn: () =>
      unwrap(
        api.GET("/v1/industry-templates/{key}/preview", {
          params: { path: { key: templateKey as IndustryTemplateKey }, query: { goals: goalsKey ? (goalsKey.split(",") as Goal[]) : undefined, language: lang } },
        }),
      ),
    enabled: Boolean(templateKey),
    staleTime: Infinity,
    placeholderData: (previous, previousQuery) => (previousQuery?.queryKey[TEMPLATE_KEY_INDEX] === templateKey ? previous : undefined),
  });
}
